import { useQuery, useLazyQuery } from 'react-apollo';

// Define the root nodes which should not be cached
const DYNAMIC_QUERY_NODES = [
  'viewer',
  'order',
  'hotelDetails',
  'flightSearchCriteria',
  'authentication',
];

/**
 * This function probably belongs in the schema repo
 *
 * @param {Object} queryAst a AST Object representing a graphql Document
 * @returns {Boolean}
 */
const isDynamicQuery = ({ definitions }) => {
  // Returns a flat array of all query fields
  const queryFields = definitions
    .filter(({ operation }) => operation === 'query')
    .map(({ selectionSet }) => selectionSet)
    .reduce((flatArr, { selections }) => flatArr.concat(selections), []);

  const dynamicQueryFields = queryFields.filter(({ name }) =>
    DYNAMIC_QUERY_NODES.includes(name.value)
  );

  return dynamicQueryFields.length > 0;
};

/**
 * useSSRQuery
 *
 * This hook is a drop-in replacement for `react-apollo`'s `useQuery` hook.
 *
 * It exists to help avoid the inadvertent inclusion of private information and queries which should be dynamic (i.e. not cached) in SSR rendered output.
 * It does this by inspecting the query AST passed in the `query` prop and testing it for root query nodes which should not be included in SSR rendered output.
 *
 * It may be that as the schema evolves more conditions will have to be evaluated in this simple wrapper.
 * To add more conditions add the root node name to the `DYNAMIC_QUERY_NODES` array.
 *
 * By default the fetch policy is `cache-first` but this can be overridden depending on your requirements by passing an `options.fetchPolicy` value.
 * Documentation about different fetch policies can be found here: https://www.apollographql.com/docs/react/api/react-apollo/#optionsfetchpolicy.
 *
 */

const getSSRQueryHook = useApolloQueryHook => (query, options) => {
  const isSSR = !isDynamicQuery(query);

  return useApolloQueryHook(query, {
    ...options,
    ssr: isSSR,
  });
};

const useSSRQuery = getSSRQueryHook(useQuery);
const useLazySSRQuery = getSSRQueryHook(useLazyQuery);

export { useSSRQuery, useLazySSRQuery };
